import { ArtistsNames } from 'components/artists/ArtistsNames'
import { Icon } from 'components/common/Icon'
import { NaturalImage } from 'components/common/NaturalImage'
import { Price } from 'components/common/Price'
import { Separator } from 'components/common/Separator'
import { TradeBreakdown } from 'components/common/TradeBreakdown'
import { Currencies } from 'constants/currencies'
import routes from 'constants/routes'
import { useCurrencies } from 'contexts'
import { useTradeFees } from 'hooks/useTradeFees'
import useTranslation from 'next-translate/useTranslation'
import { StaticImageData } from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useState } from 'react'
import { TradeType } from 'types'
import { Artwork, Edition } from 'utils/data'
import { getArtworkUrl } from 'utils/urls'
import { useUserProfile } from '../../hooks'
import { getFeesFromProfile } from '../../utils/fees'

type Props = {
  price: number
  priceGbp: number
  artwork: Artwork
  imageUrl: string | StaticImageData
  currentEdition?: Edition
  isSell?: boolean
  headerClassName?: string
  bodyClassName?: string
  showPriceWhenCollapsed?: boolean
  country?: string
}

export const SaleSummary = ({
  price,
  priceGbp,
  artwork: { name, artists, id },
  imageUrl,
  currentEdition,
  isSell,
  headerClassName,
  bodyClassName,
  showPriceWhenCollapsed,
  country,
}: Props) => {
  const { t } = useTranslation('review_trade_form')
  const router = useRouter()
  const { tradeType, DHLInsurance } = router.query
  const [isExpanded, setIsExpanded] = useState(false)
  const { data: userProfile } = useUserProfile()

  const { userCurrency } = useCurrencies()

  const fees = getFeesFromProfile(userProfile)

  const { tradeFees, total } = useTradeFees({
    fees,
    tradeValue: price,
    tradeValueGbp: priceGbp,
    tradeType: String(tradeType) as TradeType,
    DHLInsurance: DHLInsurance === 'true',
    edition: currentEdition,
    country,
  })

  const isSubmitDocsPage = router.asPath.startsWith(
    routes.askOrSell.submitDocuments
  )

  return (
    <>
      <div
        className={`flex justify-between px-6 py-4 md:px-12 ${headerClassName}`}
      >
        <button
          type='button'
          className='flex items-center space-x-3 text-sm text-secondary-default'
          onClick={() => setIsExpanded(prevState => !prevState)}
        >
          <span>
            {isExpanded ? (
              <>{isSell ? t('hide_sale_summary') : t('hide_listing_summary')}</>
            ) : (
              <>{isSell ? t('show_sale_summary') : t('show_listing_summary')}</>
            )}
          </span>
          <Icon name={isExpanded ? 'arrow-chevron-up' : 'arrow-chevron-down'} />
        </button>

        {(!isExpanded || showPriceWhenCollapsed) && (
          <Price
            className='text-lg font-medium'
            formattingOptions={{
              minimumFractionDigits: 2,
              from: Currencies.USER,
            }}
          >
            {price}
          </Price>
        )}
      </div>

      {!isExpanded && <Separator color='cool-300' />}

      {isExpanded && (
        <div
          className={`border border-cool-300 bg-cool-150 p-5 ${bodyClassName}`}
        >
          <div className='flex space-x-5'>
            <div className='relative h-16 w-16'>
              <NaturalImage
                src={imageUrl}
                priority
                fill
                alt={`Artwork - ${name}`}
                sizes='5vw'
                className='object-contain'
              />
            </div>
            <div className='flex flex-col justify-center'>
              <Link
                href={getArtworkUrl(artists, name, id)}
                className='text-sm font-medium hover:text-secondary-default'
              >
                {name}
              </Link>
              <ArtistsNames artists={artists} linkClassName='!text-xs' />
            </div>
          </div>

          <Separator className='my-5' color='cool-300' />

          <div className='flex flex-col space-y-5'>
            <TradeBreakdown
              artwork={{ name, artists }}
              tradeValue={price}
              tradeType={isSubmitDocsPage ? 'sell' : (tradeType as string)}
              withArtworkDetails={false}
              bodyClassName='flex flex-col space-y-3'
              summaryClassName='!text-sm'
              tradeFees={tradeFees}
              total={total}
              userCurrency={userCurrency}
            />
          </div>
        </div>
      )}
    </>
  )
}
